import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../http-client';

export interface PartnerSearchResult {
  id: string;
  display_first_name: string | null;
  display_last_name: string | null;
  first_name: string | null;
  last_name: string | null;
  primary_email: string | null;
  /**
   * Eligibility flags — populated only when `eventId` is supplied to
   * `usePartnerSearch`. The picker uses these to disable selection for
   * contestants who have already hit the event's per-contestant entry
   * cap, so the inviter sees the constraint up-front instead of getting
   * a 409 PARTNER_AT_MAX_ENTRIES after submitting.
   */
  at_max_entries?: boolean;
  active_entry_count?: number;
  max_entries?: number;
}

export interface PartnerSearchOptions {
  gender?: string;
  minPartnerAge?: number;
  /**
   * Optional event UUID. When supplied, results include `at_max_entries`
   * + active entry counts so the partner picker can disable contestants
   * who already have an active entry up to the event's per-contestant cap.
   */
  eventId?: string | null;
  /**
   * Profile id of the registrant — the user the registration is for —
   * which the search excludes from results (you can't be your own
   * partner). In self-registration this is the actor's own profile id;
   * in the on-behalf buddy-group flow it's the teammate being registered
   * (so the actor *can* appear as a candidate partner for them).
   * Omit to fall back to the backend's default actor-self-exclusion.
   */
  excludeUserProfileId?: string | null;
}

export function usePartnerSearch(query: string, options?: PartnerSearchOptions) {
  const { gender, minPartnerAge, eventId, excludeUserProfileId } = options ?? {};

  return useQuery({
    queryKey: [
      'partner-search',
      query,
      gender,
      minPartnerAge,
      eventId ?? null,
      excludeUserProfileId ?? null,
    ],
    queryFn: async () => {
      let url = `/registration-ops/search-users?q=${encodeURIComponent(query)}&limit=25`;
      if (gender) {
        url += `&gender=${encodeURIComponent(gender)}`;
      }
      if (minPartnerAge != null) {
        url += `&min_partner_age=${minPartnerAge}`;
      }
      if (eventId) {
        url += `&event_id=${encodeURIComponent(eventId)}`;
      }
      if (excludeUserProfileId) {
        url += `&exclude_user_profile_id=${encodeURIComponent(excludeUserProfileId)}`;
      }
      const res = await httpClient.get<{ data: PartnerSearchResult[] }>(url);
      return ((res as any)?.data ?? res ?? []) as PartnerSearchResult[];
    },
    enabled: query.length >= 2,
    staleTime: 30_000,
  });
}

export interface PartnerCostReconciliation {
  applies: boolean;
  original_partner_allocation: number;
  new_partner_cost: number;
  /** Signed delta. Positive = primary owes; negative = primary will be refunded. */
  diff: number;
  adjustment_fee: number;
  direction: 'charge' | 'refund' | 'noop';
  /** Net amount the primary will be charged or refunded after adjustment_fee. */
  net_amount: number;
  explanation: string;
}

export interface PartnerFeePreview {
  partner_user_profile_id: string;
  partner_display_name: string | null;
  is_member: boolean;
  member_fee_enabled: boolean;
  organization_id: string | null;
  organization_name: string | null;
  entry_fee: number;
  transaction_fee: number;
  non_member_surcharge: number;
  total_fee: number;
  /**
   * Populated only when `primaryRegistrationId` is supplied AND the
   * primary's row is bound to a completed bulk capture (paid_for_partner
   * flow). Drives the "you'll be charged / refunded" disclosure on
   * FindNewPartnerDialog.
   */
  cost_reconciliation: PartnerCostReconciliation | null;
}

/**
 * Look up a candidate partner's actual per-side cost for a specific
 * event so the registration wizard can show real numbers (membership-
 * aware) before the user clicks Pay. Without this, the wizard quoted
 * the partner side at the primary's totalFee, which surprised testers
 * when Stripe Elements opened with a different amount because the
 * partner's membership status differed.
 *
 * `primaryRegistrationId` is optional: when supplied, the response
 * includes a `cost_reconciliation` block that says exactly what will
 * move on accept (charge, refund, or noop, plus the flat partner-swap
 * adjustment fee). FindNewPartnerDialog passes it; the wizard's
 * partner picker on initial registration leaves it null.
 */
export function usePartnerFeePreview(
  eventId: string | null | undefined,
  partnerUserProfileId: string | null | undefined,
  primaryRegistrationId?: string | null,
) {
  return useQuery({
    queryKey: [
      'partner-fee-preview',
      eventId,
      partnerUserProfileId,
      primaryRegistrationId ?? null,
    ],
    queryFn: async (): Promise<PartnerFeePreview> => {
      const params = new URLSearchParams({
        event_id: eventId as string,
        partner_user_profile_id: partnerUserProfileId as string,
      });
      if (primaryRegistrationId) {
        params.set('primary_registration_id', primaryRegistrationId);
      }
      const res = await httpClient.get<PartnerFeePreview>(
        `/registration-ops/partner-fee-preview?${params.toString()}`,
      );
      return res as PartnerFeePreview;
    },
    enabled: !!eventId && !!partnerUserProfileId,
    staleTime: 30_000,
  });
}
